import type {
  DeviceSyncScope,
  WorkspaceHydrationReceipt,
} from "./workspaceHydration.js";

export interface StoredWorkspaceHydrationReceipt {
  attemptId: string;
  revision: number;
  startedAt: number;
  updatedAt: number;
  receipt: WorkspaceHydrationReceipt;
  /** History never proves present remote content or device approval. */
  verified: false;
  resumable: boolean;
}
export interface WorkspaceHydrationAttempt {
  scope: DeviceSyncScope;
  attemptId: string;
  revision: number;
  startedAt: number;
}
export class WorkspaceHydrationJournalError extends Error {
  constructor(
    public readonly code: "unavailable" | "superseded" | "corrupt",
    message: string,
  ) {
    super(message);
    this.name = "WorkspaceHydrationJournalError";
  }
}
export const WORKSPACE_HYDRATION_JOURNAL_DB = "yada-device-sync-journal";
const DATABASE = WORKSPACE_HYDRATION_JOURNAL_DB,
  HEADS = "heads",
  HISTORY = "history";
function unavailable() {
  return new WorkspaceHydrationJournalError(
    "unavailable",
    "Device sync progress could not be saved in this browser. Existing keys and local changes remain intact. Allow browser storage and retry sync.",
  );
}
function corrupt() {
  return new WorkspaceHydrationJournalError(
    "corrupt",
    "Saved device sync progress is invalid. Existing keys and local changes remain intact.",
  );
}
function scopeKey(scope: DeviceSyncScope) {
  if (
    !scope ||
    typeof scope.accountId !== "string" ||
    !scope.accountId ||
    typeof scope.deviceId !== "string" ||
    !scope.deviceId ||
    !(
      scope.profileId === null ||
      (typeof scope.profileId === "string" && !!scope.profileId)
    )
  )
    throw corrupt();
  return JSON.stringify([scope.accountId, scope.profileId, scope.deviceId]);
}
function natural(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}
function receiptValid(value: WorkspaceHydrationReceipt) {
  if (
    !value ||
    !["running", "complete", "incomplete"].includes(value.status) ||
    typeof value.complete !== "boolean"
  )
    return false;
  scopeKey(value.scope);
  if (
    ![
      "totalWorkspaces",
      "keyReadyWorkspaces",
      "syncedWorkspaces",
      "emptyWorkspaces",
      "syncedDocuments",
    ].every((key) => natural(value[key as keyof WorkspaceHydrationReceipt]))
  )
    return false;
  if (
    value.syncedWorkspaces > value.keyReadyWorkspaces ||
    value.keyReadyWorkspaces > value.totalWorkspaces ||
    !Array.isArray(value.failedWorkspaces)
  )
    return false;
  if (
    !value.failedWorkspaces.every(
      (f) =>
        f &&
        typeof f.workspaceId === "string" &&
        ["keys", "documents", "discovery"].includes(f.stage) &&
        typeof f.message === "string",
    )
  )
    return false;
  return value.status === "running"
    ? !value.complete && value.finishedAt === null
    : natural(value.finishedAt) &&
        (value.complete
          ? value.status === "complete" &&
            !value.failedWorkspaces.length &&
            value.syncedWorkspaces === value.totalWorkspaces
          : value.status === "incomplete");
}
/** Explicit allowlist prevents errors, tokens, keys, and content entering history. */
function nonsecretReceipt(
  receipt: WorkspaceHydrationReceipt,
): WorkspaceHydrationReceipt {
  if (!receiptValid(receipt)) throw corrupt();
  return {
    scope: {
      accountId: receipt.scope.accountId,
      profileId: receipt.scope.profileId,
      deviceId: receipt.scope.deviceId,
    },
    totalWorkspaces: receipt.totalWorkspaces,
    keyReadyWorkspaces: receipt.keyReadyWorkspaces,
    syncedWorkspaces: receipt.syncedWorkspaces,
    emptyWorkspaces: receipt.emptyWorkspaces,
    syncedDocuments: receipt.syncedDocuments,
    failedWorkspaces: receipt.failedWorkspaces.map((f) => ({
      workspaceId: f.workspaceId,
      stage: f.stage,
      message:
        f.stage === "keys"
          ? "Original encryption keys unavailable"
          : f.stage === "documents"
            ? "Document sync incomplete"
            : "Account discovery incomplete",
    })),
    complete: receipt.complete,
    status: receipt.status,
    finishedAt: receipt.finishedAt,
  };
}
interface Head {
  attemptId: string;
  revision: number;
  startedAt: number;
  updatedAt: number;
  snapshotKey: string;
}
function validateHead(head: Head, key: string) {
  if (
    !head ||
    typeof head.attemptId !== "string" ||
    !head.attemptId ||
    !natural(head.revision) ||
    !head.revision ||
    !natural(head.startedAt) ||
    !natural(head.updatedAt) ||
    head.updatedAt < head.startedAt ||
    head.snapshotKey !== JSON.stringify([key, head.revision])
  )
    throw corrupt();
}
function validateSnapshot(
  value: StoredWorkspaceHydrationReceipt,
  key: string,
  head?: Head,
) {
  if (
    !value ||
    typeof value.attemptId !== "string" ||
    !value.attemptId ||
    !natural(value.revision) ||
    !value.revision ||
    !natural(value.startedAt) ||
    !natural(value.updatedAt) ||
    value.updatedAt < value.startedAt ||
    !receiptValid(value.receipt) ||
    scopeKey(value.receipt.scope) !== key ||
    value.verified !== false ||
    value.resumable !== (value.receipt.status !== "complete")
  )
    throw corrupt();
  if (
    head &&
    (value.attemptId !== head.attemptId ||
      value.revision !== head.revision ||
      value.startedAt !== head.startedAt ||
      value.updatedAt !== head.updatedAt)
  )
    throw corrupt();
}
async function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let settled = false;
    try {
      const request = indexedDB.open(DATABASE, 1);
      const fail = () => {
        if (!settled) {
          settled = true;
          reject(unavailable());
        }
      };
      request.onupgradeneeded = () => {
        request.result.createObjectStore(HEADS);
        request.result.createObjectStore(HISTORY);
      };
      request.onerror = request.onblocked = fail;
      request.onsuccess = () => {
        if (settled) {
          request.result.close();
          return;
        }
        settled = true;
        resolve(request.result);
      };
    } catch {
      reject(unavailable());
    }
  });
}
type Guard = (callback: () => void) => () => void;
async function transact<T>(
  mode: IDBTransactionMode,
  body: (
    tx: IDBTransaction,
    set: (value: T) => void,
    guard: Guard,
    fail: (error: WorkspaceHydrationJournalError) => void,
  ) => void,
): Promise<T> {
  const database = await open();
  return new Promise((resolve, reject) => {
    let transaction: IDBTransaction,
      result: T,
      failure: WorkspaceHydrationJournalError | undefined;
    try {
      transaction = database.transaction([HEADS, HISTORY], mode, {
        durability: "strict",
      });
    } catch {
      database.close();
      reject(unavailable());
      return;
    }
    const fail = (error: WorkspaceHydrationJournalError) => {
      failure = error;
      try {
        transaction.abort();
      } catch {
        database.close();
        reject(error);
      }
    };
    const guard: Guard = (callback) => () => {
      try {
        callback();
      } catch (error) {
        fail(
          error instanceof WorkspaceHydrationJournalError
            ? error
            : unavailable(),
        );
      }
    };
    transaction.oncomplete = () => {
      database.close();
      resolve(result);
    };
    transaction.onabort = () => {
      database.close();
      reject(failure || unavailable());
    };
    transaction.onerror = () => {
      failure ||= unavailable();
    };
    guard(() =>
      body(
        transaction,
        (value) => {
          result = value;
        },
        guard,
        fail,
      ),
    )();
  });
}
function readHead(
  tx: IDBTransaction,
  key: string,
  guard: Guard,
  done: (head: Head | undefined) => void,
) {
  const request = tx.objectStore(HEADS).get(key);
  request.onsuccess = guard(() => {
    const head = request.result as Head | undefined;
    if (head === undefined) {
      done(undefined);
      return;
    }
    validateHead(head, key);
    const snapshot = tx.objectStore(HISTORY).get(head.snapshotKey);
    snapshot.onsuccess = guard(() => {
      validateSnapshot(snapshot.result, key, head);
      done(head);
    });
  });
}
function append(
  tx: IDBTransaction,
  key: string,
  head: Head,
  receipt: WorkspaceHydrationReceipt,
  guard: Guard,
) {
  tx.objectStore(HISTORY).add(
    {
      attemptId: head.attemptId,
      revision: head.revision,
      startedAt: head.startedAt,
      updatedAt: head.updatedAt,
      receipt: nonsecretReceipt(receipt),
      verified: false,
      resumable: receipt.status !== "complete",
    },
    head.snapshotKey,
  );
  tx.objectStore(HEADS).put(head, key);
  // Bounded append-only history: prune oldest snapshots, never modify retained rows.
  const store = tx.objectStore(HISTORY),
    request = store.getAllKeys();
  request.onsuccess = guard(() => {
    const scoped = request.result
      .filter((value) => {
        if (typeof value !== "string") throw corrupt();
        let parsed;
        try {
          parsed = JSON.parse(value);
        } catch {
          throw corrupt();
        }
        if (
          !Array.isArray(parsed) ||
          parsed.length !== 2 ||
          typeof parsed[0] !== "string" ||
          !natural(parsed[1])
        )
          throw corrupt();
        return parsed[0] === key;
      })
      .sort((a, b) => JSON.parse(a as string)[1] - JSON.parse(b as string)[1]);
    for (const old of scoped.slice(0, Math.max(0, scoped.length - 64)))
      store.delete(old);
  });
}
interface AttemptStartedNotice {
  scope: DeviceSyncScope;
  attemptId: string;
}
const attemptListeners = new Set<(notice: AttemptStartedNotice) => void>();
let attemptChannel: BroadcastChannel | null = null;
function notifyAttemptStarted(notice: AttemptStartedNotice) {
  const snapshot = Object.freeze({
    scope: Object.freeze({ ...notice.scope }),
    attemptId: notice.attemptId,
  });
  for (const listener of attemptListeners) {
    try {
      listener(snapshot);
    } catch {
      /* A view cannot alter persistence. */
    }
  }
}
function ensureAttemptChannel() {
  if (
    attemptChannel ||
    typeof window === "undefined" ||
    typeof window.BroadcastChannel !== "function"
  )
    return;
  try {
    attemptChannel = new window.BroadcastChannel("yada-device-sync-attempts");
    attemptChannel.onmessage = (event) => {
      const notice = event.data as AttemptStartedNotice;
      try {
        scopeKey(notice?.scope);
        if (typeof notice.attemptId !== "string" || !notice.attemptId) return;
        notifyAttemptStarted({
          scope: { ...notice.scope },
          attemptId: notice.attemptId,
        });
      } catch {
        /* Ignore malformed nonsecret invalidation notices. */
      }
    };
  } catch {
    /* Fencing remains authoritative when cross-tab notices are unavailable. */
  }
}
/** Notices only invalidate live evidence; they cannot establish progress or completion. */
export function onWorkspaceHydrationAttemptStarted(
  callback: (notice: AttemptStartedNotice) => void,
) {
  attemptListeners.add(callback);
  ensureAttemptChannel();
  return () => {
    attemptListeners.delete(callback);
  };
}
export async function beginWorkspaceHydrationAttempt(
  receipt: WorkspaceHydrationReceipt,
): Promise<WorkspaceHydrationAttempt> {
  const scope = { ...receipt.scope },
    key = scopeKey(scope),
    attemptId = crypto.randomUUID(),
    startedAt = Date.now();
  const attempt = await transact<WorkspaceHydrationAttempt>(
    "readwrite",
    (tx, set, guard) =>
      readHead(tx, key, guard, (head) => {
        const revision = (head?.revision || 0) + 1;
        append(
          tx,
          key,
          {
            attemptId,
            revision,
            startedAt,
            updatedAt: startedAt,
            snapshotKey: JSON.stringify([key, revision]),
          },
          receipt,
          guard,
        );
        set({ scope, attemptId, revision, startedAt });
      }),
  );
  ensureAttemptChannel();
  const notice = { scope, attemptId };
  notifyAttemptStarted(notice);
  try {
    attemptChannel?.postMessage(notice);
  } catch {
    /* Journal fencing does not depend on delivery. */
  }
  return attempt;
}
export async function checkpointWorkspaceHydrationAttempt(
  attempt: WorkspaceHydrationAttempt,
  receipt: WorkspaceHydrationReceipt,
): Promise<WorkspaceHydrationAttempt> {
  const key = scopeKey(attempt.scope);
  if (scopeKey(receipt.scope) !== key) throw corrupt();
  return transact("readwrite", (tx, set, guard, fail) =>
    readHead(tx, key, guard, (head) => {
      if (
        !head ||
        head.attemptId !== attempt.attemptId ||
        head.revision !== attempt.revision
      ) {
        fail(
          new WorkspaceHydrationJournalError(
            "superseded",
            "A newer device sync attempt started in another tab. Continue there or retry here.",
          ),
        );
        return;
      }
      const revision = head.revision + 1,
        updatedAt = Math.max(Date.now(), head.updatedAt);
      append(
        tx,
        key,
        {
          ...head,
          revision,
          updatedAt,
          snapshotKey: JSON.stringify([key, revision]),
        },
        receipt,
        guard,
      );
      set({ ...attempt, revision });
    }),
  );
}
/** Check fencing before beginning another external action; never advances a revision. */
export async function assertWorkspaceHydrationAttemptCurrent(
  attempt: WorkspaceHydrationAttempt,
): Promise<void> {
  const key = scopeKey(attempt.scope);
  return transact("readonly", (tx, set, guard, fail) =>
    readHead(tx, key, guard, (head) => {
      if (
        !head ||
        head.attemptId !== attempt.attemptId ||
        head.revision !== attempt.revision
      ) {
        fail(
          new WorkspaceHydrationJournalError(
            "superseded",
            "A newer device sync attempt started in another tab. Continue there or retry here.",
          ),
        );
        return;
      }
      set(undefined);
    }),
  );
}
export async function readStoredWorkspaceHydrationReceipt(
  scope: DeviceSyncScope,
): Promise<StoredWorkspaceHydrationReceipt | null> {
  const key = scopeKey(scope);
  return transact("readonly", (tx, set, guard) => {
    const headsStore = tx.objectStore(HEADS);
    const historyStore = tx.objectStore(HISTORY);

    const loadSnapshot = (targetHead: Head, targetKey: string) => {
      validateHead(targetHead, targetKey);
      const snapshotReq = historyStore.get(targetHead.snapshotKey);
      snapshotReq.onsuccess = guard(() => {
        validateSnapshot(snapshotReq.result, targetKey, targetHead);
        set(snapshotReq.result);
      });
    };

    const directReq = headsStore.get(key);
    directReq.onsuccess = guard(() => {
      if (directReq.result) {
        loadSnapshot(directReq.result as Head, key);
        return;
      }
      const allKeysReq = headsStore.getAllKeys();
      allKeysReq.onsuccess = guard(() => {
        const allKeys = allKeysReq.result as string[];
        let matchedKey: string | null = null;
        if (scope.profileId !== null) {
          const fallbackKey = scopeKey({ ...scope, profileId: null });
          if (allKeys.includes(fallbackKey)) matchedKey = fallbackKey;
        } else {
          matchedKey =
            allKeys.find((candidateKey) => {
              try {
                const parsed = JSON.parse(candidateKey);
                return (
                  Array.isArray(parsed) &&
                  parsed[0] === scope.accountId &&
                  parsed[2] === scope.deviceId
                );
              } catch {
                return false;
              }
            }) || null;
        }

        if (!matchedKey) {
          set(null);
          return;
        }

        const matchedReq = headsStore.get(matchedKey);
        matchedReq.onsuccess = guard(() => {
          if (!matchedReq.result) {
            set(null);
            return;
          }
          loadSnapshot(matchedReq.result as Head, matchedKey!);
        });
      });
    });
  });
}
/** Diagnostic history cannot grant approval or verified completion. */
export async function readWorkspaceHydrationHistory(
  scope: DeviceSyncScope,
): Promise<StoredWorkspaceHydrationReceipt[]> {
  const key = scopeKey(scope);
  return transact("readonly", (tx, set, guard) => {
    const request = tx.objectStore(HISTORY).getAll();
    request.onsuccess = guard(() => {
      let rows = request.result.filter(
        (row) => scopeKey(row.receipt?.scope) === key,
      );
      if (rows.length === 0) {
        if (scope.profileId !== null) {
          const fallbackKey = scopeKey({ ...scope, profileId: null });
          rows = request.result.filter(
            (row) => scopeKey(row.receipt?.scope) === fallbackKey,
          );
        } else {
          rows = request.result.filter((row) => {
            const s = row.receipt?.scope;
            return (
              s &&
              s.accountId === scope.accountId &&
              s.deviceId === scope.deviceId
            );
          });
        }
      }
      for (const row of rows)
        validateSnapshot(row, scopeKey(row.receipt?.scope));
      set(rows.sort((a, b) => a.revision - b.revision));
    });
  });
}
